import { useState, useEffect, useCallback, type RefObject } from 'react'

/**
 * Detects whether the background behind a given element is dark or light.
 * Walks up the DOM tree to find the first non-transparent background color.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  const check = useCallback(() => {
    const element = elementRef.current
    if (!element) return

    const rect = element.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2

    // Hide element to find what's behind it
    const hidden = element.style.visibility
    element.style.visibility = 'hidden'
    const behind = document.elementFromPoint(x, y)
    element.style.visibility = hidden

    if (!behind) return

    // Walk up the tree to find a non-transparent background
    let node: Element | null = behind
    while (node && node !== document.documentElement) {
      const style = getComputedStyle(node)
      const bg = style.backgroundColor

      // Parse rgba/rgb
      const match = bg.match(/[\d.]+/g)
      if (match && match.length >= 3) {
        const r = parseFloat(match[0])
        const g = parseFloat(match[1])
        const b = parseFloat(match[2])
        const a = match.length >= 4 ? parseFloat(match[3]) : 1

        // Skip transparent backgrounds
        if (a > 0.1) {
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
          setBackground(luminance < 0.5 ? 'dark' : 'light')
          return
        }
      }

      node = node.parentElement
    }

    // Default: assume light (white page background)
    setBackground('light')
  }, [elementRef])

  useEffect(() => {
    check()

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [check])

  return background
}
